import { Box, Divider, Stack, Typography } from '@mui/material';
import { Fragment, Suspense, lazy, useCallback, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import {
  AllPointOfInterestsForDataLoaderDocument,
  PanelFeatureEnum,
  PanelItemFieldTypeEnum,
  PoiAccessibilityEnum,
  PoiStatus,
  SelectedPointOfInterestDocument,
  UpdatePointOfInterestInput,
  useUpdatePoiGeneralFieldsMutation,
  useUpdatePoiTemplateFieldsMutation,
  useUpdatePointOfInterestMutation,
} from '@/__generated__/graphql';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { useDrawerWidths } from '@/hooks/useDrawerWidths';
import { useTemplateFields } from '../../hooks/useTemplateFields';
import { useGeneralFields } from '../../hooks/useGeneralFields';
import { useBlisterData } from '../../hooks/useBlisterData';
import { useUncertainField } from '../../hooks/useUncertainField';
import { useTriggerPoiEditing } from '../../hooks';
import * as poiState from '../../../../state';
import { SaveCancelFormButtons } from '../SaveCancelFormButtons';
import { StyledForm } from '../UpdateGeneralFields/styles';
import { saveCancelButtonCommonStyles } from '../../../styles';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import { convertTemplateNameToPoiType, getPoiTemplateKey, convertTimeStampToDate } from '@/utils';
import { GeneralFieldsType } from '../../types';
import { abyssColors } from '@/theme/colors';
import { PanelItemEditor } from './PanelItemEditor';
import { isItemEditable, sectionHasFeature } from './panelEditability';

const BlisterMeasurementLazy = lazy(() =>
  import('../../../../createOrEdit').then(({ BlisterMeasurement }) => ({
    default: BlisterMeasurement,
  }))
);

export const UpdateSectionFields = () => {
  const editPointOfInterest = useAtomValue(poiState.editPointOfInterest);
  const poi = editPointOfInterest?.pointOfInterest;
  const template = editPointOfInterest?.template;
  const editingSection = editPointOfInterest?.editingSection;

  // Resolve section from the current template's panelConfig so toggling Pit/Blister swaps the
  // rendered fields. Falls back to the snapshot stored on the atom if no matching key exists.
  const section = useMemo(() => {
    if (!editingSection?.key) return editingSection;
    const fromCurrentTemplate = (template?.panelConfig?.tabs ?? [])
      .flatMap((tab) => tab.sections ?? [])
      .find((s) => s.key === editingSection.key);
    return fromCurrentTemplate ?? editingSection;
  }, [editingSection, template?.panelConfig]);

  const navbarHeight = useNavbarHeight();
  const { showSnackBar } = useSnackBarMessage({});
  const { cancelEditing } = useTriggerPoiEditing();
  const [loading, setLoading] = useState(false);

  const poiType = convertTemplateNameToPoiType(getPoiTemplateKey(template));

  const allItems = (section?.groups ?? []).flatMap((group) =>
    group.feature ? [] : group.items.filter((item) => !item.feature)
  );

  const templateItems = allItems.filter(
    (item) => item.fieldType === PanelItemFieldTypeEnum.TemplateField
  );
  const generalItems = allItems.filter(
    (item) => item.fieldType === PanelItemFieldTypeEnum.PointOfInterest
  );
  const blisterItems = allItems.filter((item) => item.fieldType === PanelItemFieldTypeEnum.Blister);

  const sectionTemplateFields = (template?.fields ?? []).filter((field) =>
    templateItems.some((item) => item.key === field.key)
  );

  const templateDataHook = useTemplateFields({
    selectedPoiInfo: poi,
    templateFields: sectionTemplateFields,
  });

  const blisterDataHook = useBlisterData(templateDataHook, poiType);
  const { localBlisterData } = blisterDataHook;
  const uncertainField = useUncertainField({ dataHook: templateDataHook });
  const { rightDrawerWidth, doublePanelWidth } = useDrawerWidths();
  const hasMarkAsPit = sectionHasFeature(section, PanelFeatureEnum.MarkAsPit);
  const hasManualSetHeight = sectionHasFeature(section, PanelFeatureEnum.ManualSetHeight);
  const hasRegenerateMeasurement = sectionHasFeature(
    section,
    PanelFeatureEnum.RegenerateMeasurement
  );
  const hasBlisterFeature = hasMarkAsPit || hasManualSetHeight || hasRegenerateMeasurement;
  const showRegenerateMeasurementPanel = hasRegenerateMeasurement && Boolean(localBlisterData);

  const formattedDueDate = poi?.dueDate ? convertTimeStampToDate(poi.dueDate) : undefined;
  const hasGeneralKey = (key: string) => generalItems.some((item) => item.key === key);

  const initialGeneralState: GeneralFieldsType = {
    name: hasGeneralKey('name') ? (poi?.name ?? undefined) : undefined,
    status: hasGeneralKey('status') ? ((poi?.status as PoiStatus) ?? undefined) : undefined,
    visibility: hasGeneralKey('visibility')
      ? (poi?.visibility as GeneralFieldsType['visibility'])
      : undefined,
    accessibility: hasGeneralKey('accessibility')
      ? ((poi?.accessibility as PoiAccessibilityEnum) ?? undefined)
      : undefined,
    assignee: hasGeneralKey('assignee') ? (poi?.assignee ?? undefined) : undefined,
    dueDate: hasGeneralKey('dueDate') ? formattedDueDate : undefined,
    comment: hasGeneralKey('comment') ? (poi?.comment ?? undefined) : undefined,
    workpackNumber: hasGeneralKey('workpackNumber')
      ? (poi?.workpackNumber ?? undefined)
      : undefined,
  };

  const generalFieldsData = useGeneralFields(initialGeneralState);

  const [mutateTemplateFields] = useUpdatePoiTemplateFieldsMutation();
  const [mutateUpdatePointOfInterest] = useUpdatePointOfInterestMutation();
  const [mutateGeneralFields] = useUpdatePoiGeneralFieldsMutation();

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLElement>) => {
      event.preventDefault();
      setLoading(true);

      const mutationTasks: Array<{ name: string; run: () => Promise<unknown> }> = [];

      if (templateItems.length > 0 || blisterItems.length > 0 || hasBlisterFeature) {
        const sectionFieldData = Object.entries(templateDataHook.state).reduce<
          Record<string, string | number>
        >((acc, [key, value]) => {
          acc[key] = value;
          return acc;
        }, {});
        const templateFieldData = {
          ...(poi?.templateFieldData ?? {}),
          ...sectionFieldData,
        };

        if (poi?.blister && localBlisterData && (blisterItems.length > 0 || hasBlisterFeature)) {
          const blister = JSON.parse(JSON.stringify(localBlisterData), (key, value) =>
            key === '__typename' ? undefined : value
          );
          mutationTasks.push({
            name: 'updatePointOfInterest',
            run: () =>
              mutateUpdatePointOfInterest({
                variables: {
                  input: {
                    pointOfInterestId: poi?.id ?? '',
                    blister,
                    templateId: template?.id,
                    type: poiType,
                    templateFieldData,
                    lastModifiedFor: `Update ${section?.label ?? 'Section'} Fields`,
                  },
                },
                refetchQueries: [
                  SelectedPointOfInterestDocument,
                  AllPointOfInterestsForDataLoaderDocument,
                ],
              }),
          });
        } else {
          mutationTasks.push({
            name: 'updateTemplateFields',
            run: () =>
              mutateTemplateFields({
                variables: {
                  input: {
                    pointOfInterestId: poi?.id ?? '',
                    templateId: template?.id,
                    templateFieldData,
                    lastModifiedFor: `Update ${section?.label ?? 'Section'} Fields`,
                  },
                },
                refetchQueries: [
                  SelectedPointOfInterestDocument,
                  AllPointOfInterestsForDataLoaderDocument,
                ],
              }),
          });
        }
      }

      if (generalItems.length > 0) {
        const stringifiedDueDate = generalFieldsData.state.dueDate
          ? String(generalFieldsData.state.dueDate)
          : undefined;

        const generalInput: UpdatePointOfInterestInput = {
          pointOfInterestId: poi?.id ?? '',
          lastModifiedFor: `Update ${section?.label ?? 'Section'} Fields`,
          ...(hasGeneralKey('name') && { name: generalFieldsData.state.name }),
          ...(hasGeneralKey('status') && { status: generalFieldsData.state.status }),
          ...(hasGeneralKey('visibility') && { visibility: generalFieldsData.state.visibility }),
          ...(hasGeneralKey('accessibility') && {
            accessibility: generalFieldsData.state.accessibility,
          }),
          ...(hasGeneralKey('assignee') && { assignee: generalFieldsData.state.assignee }),
          ...(hasGeneralKey('dueDate') && { dueDate: stringifiedDueDate }),
          ...(hasGeneralKey('comment') && { comment: generalFieldsData.state.comment }),
          ...(hasGeneralKey('workpackNumber') && {
            workpackNumber: generalFieldsData.state.workpackNumber,
          }),
        };

        mutationTasks.push({
          name: 'updateGeneralFields',
          run: () =>
            mutateGeneralFields({
              variables: { input: generalInput },
              refetchQueries: [
                SelectedPointOfInterestDocument,
                AllPointOfInterestsForDataLoaderDocument,
              ],
            }),
        });
      }

      mutationTasks
        .reduce<Promise<unknown>>(
          (promiseChain, task) => promiseChain.then(() => task.run()),
          Promise.resolve()
        )
        .then(() => {
          showSnackBar('Point of interest updated', 'success');
          cancelEditing();
        })
        .catch((error_: unknown) => {
          void error_;
          showSnackBar('Could not update point of interest', 'error');
        })
        .finally(() => {
          setLoading(false);
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      templateItems,
      blisterItems,
      generalItems,
      hasBlisterFeature,
      templateDataHook.state,
      poi?.id,
      poi?.blister,
      template?.id,
      poiType,
      localBlisterData,
      generalFieldsData.state,
      section?.label,
      mutateTemplateFields,
      mutateUpdatePointOfInterest,
      mutateGeneralFields,
      showSnackBar,
      cancelEditing,
    ]
  );

  if (!poi) return null;

  const sortedGroups = [...(section?.groups ?? [])]
    .filter((group) => !group.feature)
    .sort((a, b) => a.order - b.order);

  return (
    <Stack direction="row" sx={{ height: '100%' }}>
      {showRegenerateMeasurementPanel && (
        <Stack width={doublePanelWidth - rightDrawerWidth}>
          <Suspense>
            <BlisterMeasurementLazy
              poiToAdd={poi}
              blisterData={blisterDataHook.blisterFieldsData}
              uncertainField={uncertainField}
              onMeasure={blisterDataHook.onMeasure}
              pointSelectMessage={blisterDataHook.pointSelectMessage}
              onSelectPoint={blisterDataHook.onSelectPoint}
              setLocalBlisterData={blisterDataHook.setLocalBlisterData}
              clearBlisterData={blisterDataHook.clearBlisterData}
              handleChangeControlledField={blisterDataHook.handleChangeControlledField}
            />
          </Suspense>
        </Stack>
      )}
      <Box height="100%" width={showRegenerateMeasurementPanel ? rightDrawerWidth : '100%'}>
        <StyledForm onSubmit={handleSubmit}>
          <Stack
            sx={{
              height: `calc(100vh / var(--zoom) - ${navbarHeight}px)`,
              overflowY: 'auto',
              ...(showRegenerateMeasurementPanel ? { borderLeft: '1px solid #ddd' } : {}),
            }}
          >
            <Box p={2} flex={1} mb="85px">
              <Typography variant="h5" sx={{ fontSize: '1.6rem', fontWeight: 500, mb: 2 }}>
                Edit {section?.label}
              </Typography>

              <Stack gap="1.6rem">
                {sortedGroups
                  .map((group) => ({
                    group,
                    items: [...group.items]
                      .filter((item) => isItemEditable(item, template))
                      .sort((a, b) => a.order - b.order),
                  }))
                  .filter(({ items }) => items.length > 0)
                  .map(({ group, items }, index) => (
                    <Fragment key={group.id}>
                      {index > 0 && <Divider sx={{ borderColor: abyssColors.primary[100] }} />}
                      <Stack gap="1.6rem">
                        {group.label && (
                          <Typography variant="h5" color={abyssColors.primary[500]}>
                            {group.label}
                          </Typography>
                        )}
                        {items.map((item) => (
                          <PanelItemEditor
                            key={item.id}
                            item={item}
                            poi={poi}
                            template={template}
                            generalFieldsData={generalFieldsData}
                            templateDataHook={templateDataHook}
                            blisterDataHook={blisterDataHook}
                          />
                        ))}
                      </Stack>
                    </Fragment>
                  ))}
              </Stack>
            </Box>

            <SaveCancelFormButtons
              cancelButtonLabel="Cancel"
              buttonLabel="Save"
              loadingLabel="Updating..."
              onCancel={cancelEditing}
              loading={loading}
              sx={saveCancelButtonCommonStyles.sx}
              saveButtonStyles={saveCancelButtonCommonStyles.saveButtonStyles}
              cancelButtonStyles={saveCancelButtonCommonStyles.cancelButtonStyles}
            />
          </Stack>
        </StyledForm>
      </Box>
    </Stack>
  );
};
